import { DatasheetUpnListDto } from "@common/dto/datasheet/upn/list";
import { downloadFile } from "@common/utils/file";
import client, { clientTransformed } from "@common/utils/rest";
import { DatasheetTableReportFormat } from "@modules/datasheets/components/editor/components/datasheet-element-settings/components/configure-table/configure-table.types";

type ApiTableReportFormat = DatasheetTableReportFormat & {
  default?: boolean;
  isDefault?: boolean;
};

const normalizeTableReportFormat = (
  format: ApiTableReportFormat
): DatasheetTableReportFormat => ({
  ...format,
  isDefault: format.isDefault ?? format.default ?? false,
  rows: (format.rows || []).map((row) => ({
    ...row,
    order: Number(row.order) || 0,
  })),
});

const serializeTableReportFormat = (format: DatasheetTableReportFormat) => {
  const { isDefault, ...rest } = format;
  return {
    ...rest,
    default: isDefault ?? false,
  };
};

export const getUpns = (): Promise<Array<DatasheetUpnListDto>> => {
  return new Promise((resolve) => {
    resolve([
      {
        id: 1,
        name: "1",
      },
      {
        id: 2,
        name: "2",
      },
      {
        id: 3,
        name: "3",
      },
      {
        id: 4,
        name: "4",
      },
      {
        id: 5,
        name: "5",
      },
    ]);
  });
};

export const createDatasheetFormat = async (params: {
  formatName: string;
  folderId: number;
  type: "ds" | "tds";
  tableId?: number;
}) => {
  const { type, ...body } = params;
  const response = await client({
    path:
      type === "tds"
        ? "api/datasheet/createDSFormat?type=8"
        : "api/datasheet/createDSFormat",
    method: "POST",
    entity: body,
  });
  return response.entity;
};

/** Context value for table datasheet (TDS) format load; use with tableContext for loadDSFormat. */
export const LOAD_DS_FORMAT_TABLE_CONTEXT = 8;

/**
 * loadDSFormat API – BE contract:
 * - formatId (int, required): ID of the datasheet format to load.
 * - context (int, required): 1=IDS, 2=PDS, 8=TDS.
 * - tableContext (int, optional): For context=8. Defaults to 1.
 * - globalId (int, optional): Global ID of the individual member. Properties object only when globalId > 0 and context 1 or 2.
 * Do not send tableFieldId; use globalId for TDS (context=8).
 */
export const loadDatasheetFormat = async (params: {
  formatId: number;
  context: number;
  tableContext?: number;
  globalId?: number;
  /** When > 0, only that tab's fields/texts are loaded (optimization). Omit or 0 = load all tabs. */
  tabId?: number;
  /** For context=8 (TDS) only: individual/pedigree id to send as globalId. */
  globalIdForTds?: number;
}) => {
  const isTableFormat = params.context === LOAD_DS_FORMAT_TABLE_CONTEXT;
  const { tabId, ...rest } = params;

  const requestParams = isTableFormat
    ? {
        formatId: rest.formatId,
        context: rest.context,
        tabId: 1, // For TDS, we always load all tabs
        tableContext: rest.tableContext ?? 1,
        ...(rest.globalIdForTds != null && rest.globalIdForTds > 0
          ? { globalId: rest.globalIdForTds }
          : {}),
      }
    : tabId != null && tabId > 0
    ? { ...rest, tabId }
    : rest;

  const response = await clientTransformed({
    path: "api/datasheet/loadDSFormat",
    method: "GET",
    params: requestParams,
  });
  // clientTransformed automatically extracts response.entity.data if it's the new structure
  // So response.entity is now { format: {...}, properties: null }
  // But if clientTransformed doesn't transform (old structure), response.entity might be the full response
  const entity = response.entity;

  // Handle both cases: transformed { format: {...} } or untransformed { code: 200, data: { format: {...} } }
  if (entity?.format) {
    return entity;
  } else if (entity?.data?.format) {
    return entity.data;
  } else {
    // Fallback: return entity as-is
    return entity;
  }
};

export const loadDatasheetData = async (params: {
  id: number;
  formatId: number;
  tabId: number;
  context: number;
}) => {
  const response = await clientTransformed({
    path: "api/datasheet/loadDatasheetData",
    method: "GET",
    params,
  });
  return response.entity;
};

/**
 * Load table datasheet data for a single row (TDS result).
 * Call after loadDSFormat (context=8). Uses id, formatId, tabId, tableFieldId, context, rrn.
 */
export const loadTableDatasheetData = async (params: {
  id: number;
  formatId: number;
  tabId: number;
  tableFieldId: number;
  context: number;
  rrn: number;
}) => {
  const response = await clientTransformed({
    path: "api/datasheet/loadTableDatasheetData",
    method: "GET",
    params,
  });
  return response.entity;
};

export const updateDatasheetFormat = async (params: {
  id: number;
  tabsData: Array<{
    id: number;
    oldTabId: number;
    index: number;
    name: string;
    fields?: Array<any>;
    texts?: Array<any>;
  }>;
  oldFormatId?: number;
  tds?: boolean;
}) => {
  // Extract tds from params to pass as query parameter, exclude it from request body
  const { tds, ...entity } = params;
  const response = await client({
    path: "api/datasheet/updateDatasheetFormat",
    method: "POST",
    params: {
      tds: tds ?? false,
    },
    entity,
  });
  return response.entity;
};

export type TableDatasheetFormatSummary = {
  id: number;
  name: string;
  isDefault?: boolean;
};

/**
 * Load table datasheet (TDS) formats for a given table field.
 * Backend endpoint: /api/datasheet/tableDatasheetFormats?context=&tableFieldId=
 */
export const getTableDatasheetFormats = async (params: {
  context: number;
  tableFieldId: number;
}): Promise<Array<TableDatasheetFormatSummary>> => {
  const response = await client({
    path: "api/datasheet/tableDatasheetFormats",
    method: "GET",
    params: {
      context: params.context,
      tableFieldId: params.tableFieldId,
    },
  });

  const raw = (response.entity as any)?.data ?? response.entity ?? [];
  return (raw as Array<any>).map((item) => ({
    id: item.formatId,
    name: item.formatName,
    isDefault: item.isDefault ?? item.default ?? false,
  }));
};

export const getTableReportFormats = async (params: {
  context: number;
  dsFormatId: number;
  tabId: number;
  tableFieldId: number;
}): Promise<Array<DatasheetTableReportFormat>> => {
  // Load full table *report* formats (column layouts) for Configure Table.
  // This uses the dedicated tableReportFormat endpoint which returns
  // TableReportFormatDTO objects including rows and default flags.
  const response = await clientTransformed({
    path: "api/datasheet/tableReportFormat",
    method: "GET",
    params: {
      context: params.context,
      dsFormatId: params.dsFormatId,
      tabId: params.tabId,
      tableFieldId: params.tableFieldId,
    },
  });

  const raw = (response.entity as any)?.data ?? response.entity ?? [];
  const formats = (raw as Array<any>).map(
    (item): ApiTableReportFormat => ({
      // Support both legacy and new field names.
      reportNumber: item.reportNumber ?? item.formatId ?? item.id,
      reportName: item.reportName ?? item.formatName ?? item.name,
      isDefault: item.isDefault ?? item.default ?? false,
      rows: item.rows ?? [],
    })
  );

  return formats.map(normalizeTableReportFormat);
};

export const saveTableReportFormat = async (params: {
  context: number;
  dsFormatId: number;
  tabId: number;
  tableFieldId: number;
  format: DatasheetTableReportFormat;
}) => {
  const response = await client({
    path: "api/datasheet/tableReportFormat",
    method: "POST",
    params: {
      context: params.context,
      dsFormatId: params.dsFormatId,
      tabId: params.tabId,
      tableFieldId: params.tableFieldId,
    },
    entity: serializeTableReportFormat(params.format),
  });
  return response.entity;
};

export const deleteTableReportFormat = async (params: {
  context: number;
  formatId: number;
}) => {
  const response = await client({
    path: "api/datasheet/tableReportFormat/delete",
    method: "POST",
    params,
  });
  return response.entity;
};

export const setTableReportFormatDefault = async (params: {
  context: number;
  dsFormatId: number;
  tabId: number;
  tableFieldId: number;
  reportNumber: number;
}) => {
  const response = await client({
    path: "api/datasheet/tableReportFormat/default",
    method: "POST",
    params,
  });
  return response.entity;
};

/**
 * Set the default table datasheet format for a given table field within a
 * parent datasheet format/tab. This wraps the v9 REST endpoint:
 *   /v9/api/datasheet/setDefaultFormatForTable
 */
export const setDefaultTableDatasheetFormat = async (params: {
  context: number;
  tableFieldId: number;
  parentDsFormatId: number;
  parentTabId: number;
  formatId: number;
}) => {
  const response = await client({
    path: "api/datasheet/setDefaultFormatForTable",
    method: "POST",
    params: {
      context: params.context,
      tableFieldId: params.tableFieldId,
      parentDsFormatId: params.parentDsFormatId,
      parentTabId: params.parentTabId,
      formatId: params.formatId,
    },
  });
  return response.entity;
};

export const getNotLoadedDatasheetComponents = async (params: {
  formatId: number;
  tabs: Array<number>;
  isTDS?: boolean;
}) => {
  const response = await client({
    path: "api/datasheet/getNotLoadedDatasheetComponents",
    method: "POST",
    params: {
      formatId: params.formatId,
      ...(params.isTDS ? { isTDS: true } : {}),
    },
    entity: params.tabs,
  });
  return response.entity?.data || response.entity;
};

export const saveFormatDefaults = async (params: {
  formatId: number;
  context: number;
  defaults: any; // DSFormatDefaultsDto
  tds?: boolean;
}) => {
  const response = await client({
    path: "api/datasheet/saveDSDefaults",
    method: "POST",
    params: {
      formatId: params.formatId,
      context: params.context,
      ...(params.tds !== undefined && { isTDS: params.tds }),
    },
    entity: params.defaults,
  });
  return response.entity;
};

export const exportDatasheet = async (payload: {
  formatId: number;
  context: number;
  tds: boolean;
}) => {
  const response = await fetch("api/datasheet/export", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = `Export failed: ${response.status} ${response.statusText}`;
    try {
      const text = await response.text();
      if (text) {
        message = text;
      }
    } catch {
      // ignore parsing errors and fall back to default message
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition");
  const match = contentDisposition?.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || "datasheet.xml";
  const contentType = response.headers.get("content-type") || "text/xml";

  downloadFile(blob, filename, contentType);
};

export type UpdateFieldValueParams = {
  globalId: number;
  fieldId: number;
  context: number;
  systemField: boolean;
  ds: boolean;
  value: string | number;
  rrn?: number;
  tableId?: number;
};

export type UpdateFieldValueResponse = {
  code: number;
  errors: string[];
  data: unknown;
};

export const updateFieldValue = async (
  params: UpdateFieldValueParams
): Promise<UpdateFieldValueResponse> => {
  const response = await client({
    path: "api/fields/updateValue",
    method: "POST",
    entity: {
      globalId: params.globalId,
      fieldId: params.fieldId,
      context: params.context,
      systemField: params.systemField,
      ds: params.ds,
      value: params.value,
      rrn: params.rrn ?? -1,
      tableId: params.tableId ?? 0,
    },
  });
  return response.entity as UpdateFieldValueResponse;
};

export type DatasheetTableRowRequestParams = {
  globalId: number;
  context: number;
  tableId: number; // This is the fieldId of the table field
  systemField: boolean;
  copyFromRrn?: number; // -1 for blank row, positive RRN to copy existing row
  rrn?: number; // for delete
};

export const addDatasheetTableRow = async (
  params: DatasheetTableRowRequestParams
) => {
  const response = await clientTransformed({
    path: "api/datasheet/row/add",
    method: "POST",
    params: {
      globalId: params.globalId,
      fieldId: params.tableId, // fieldId is the table field's ID
      context: params.context,
      copyRrn: params.copyFromRrn ?? -1, // -1 for new empty row, positive RRN to copy
    },
  });
  // Response is DatasheetDataDto.TableRowDataDto with rrn and data array
  return response.entity;
};

export const deleteDatasheetTableRow = async (
  params: DatasheetTableRowRequestParams
) => {
  if (params.rrn == null) {
    throw new Error("rrn is required for delete operation");
  }
  const response = await clientTransformed({
    path: "api/datasheet/row/delete",
    method: "POST",
    params: {
      globalId: params.globalId,
      fieldId: params.tableId, // fieldId is the table field's ID
      context: params.context,
      rrn: params.rrn,
    },
  });
  // Response is ApiResponse with success message
  return response.entity;
};

/**
 * Set the default datasheet format for an individual/pedigree or a folder.
 *
 * This integrates with the v9 REST endpoint:
 *   /v9/api/datasheet/setDefaultFormat?context=1&id=12&formatId=18&isFolder=false
 */
export const setDatasheetDefaultFormat = async (params: {
  context: number;
  id: number;
  formatId: number;
  /** When true, save default at folder level; when false, at pedigree/individual level. */
  folder: boolean;
}) => {
  const response = await client({
    path: "api/datasheet/setDefaultFormat",
    method: "POST",
    params: {
      context: params.context,
      id: params.id,
      formatId: params.formatId,
      isFolder: params.folder,
    },
  });
  return response.entity;
};

/** IDS datasheet context (matches BE loadDSFormat / lock API). */
export const DATASHEET_LOCK_CONTEXT_IDS = 1;
/** PDS datasheet context (family id for lock API). */
export const DATASHEET_LOCK_CONTEXT_PDS = 2;

export type DatasheetLockAcquireResult = {
  acquired: boolean;
  lock: number;
  lockUser: string | null;
};

/**
 * BE {@code DatasheetResource.acquireDatasheetLock}: POST with query {@code context} (1=IDS, 2=PDS) and {@code id}
 * (global id or family id). Response {@code { lock: 1 | -1, lockUser }}.
 */
const normalizeAcquireEntity = (entity: any): DatasheetLockAcquireResult => {
  const lock = typeof entity?.lock === "number" ? entity.lock : -1;
  return {
    acquired: lock === 1,
    lock,
    lockUser: entity?.lockUser != null ? String(entity.lockUser) : null,
  };
};

export const acquireDatasheetLock = async (params: {
  /** 1 = IDS (id = global id), 2 = PDS (id = family id) */
  context: number;
  id: number;
}): Promise<DatasheetLockAcquireResult> => {
  const response = await clientTransformed({
    path: "api/datasheet/lock/acquire",
    method: "POST",
    params: {
      context: params.context,
      id: params.id,
    },
  });
  return normalizeAcquireEntity(response.entity);
};

export const heartbeatDatasheetLock = async (params: {
  context: number;
  id: number;
}): Promise<void> => {
  await client({
    path: "api/datasheet/lock/heartbeat",
    method: "POST",
    params: {
      context: params.context,
      id: params.id,
    },
  });
};

export const releaseDatasheetLock = async (params: {
  context: number;
  id: number;
}): Promise<void> => {
  await client({
    path: "api/datasheet/lock/release",
    method: "POST",
    params: {
      context: params.context,
      id: params.id,
    },
  });
};
